import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { CoachMarks } from "@/components/coach-marks";
import { DocumentLimitReached } from "@/components/document-limit";
import { Upload, FileText, Users, Send, Check, ArrowLeft, ArrowRight, Plus, Trash2, Eye, Loader2, Download, Shield, Pen, Phone, Bot, BookOpen, Lock, Star } from "lucide-react";
import { BrandIcon } from "@/components/brand-logo";
import { isBrazil, countries, getPhoneCode } from "@/lib/countries";
import { getApiUrl } from "@/lib/config";
import { invalidateDocumentQueries } from "@/lib/live-documents";
import { ContactLookupInput, contactEmails, mapLookupContact, type LookupContact, type LookupEmail } from "@/components/contact-lookup";
import { splitPhone } from "@/lib/masks";
import { useLanguage } from "@/components/language-provider";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";
import { PdfCanvas } from "@/components/PdfCanvas";
import { PdfPreview } from "@/components/PdfPreview";
import { PENDING_UPLOAD_KEY } from "@/lib/library-client";

// WhatsApp icon
const WhatsAppIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
  </svg>
);

interface Signer {
  name: string;
  email: string;
  cpf?: string;
  phone?: string;
  phoneCountry?: string;
  sendWhatsApp?: boolean;
  // Signature position (defined by sender)
  signaturePositionX?: number;
  signaturePositionY?: number;
  signaturePageNumber?: number;
  // Signature mode: 'main' = only main signature, 'footer' = only footer, 'both' = main + footer
  signatureMode?: 'main' | 'footer' | 'both';
  positionSet?: boolean; // Track if position was set
  // Filled from a saved client: its e-mails (the default one flagged) and its CPF, locked as registered.
  contactId?: string;
  contactEmails?: LookupEmail[];
  cpfLocked?: boolean;
}

interface TempUpload {
  tempUploadId: string;
  name: string;
  type: string;
  fileUrl: string;
  previewUrl: string;
  fileSize: number;
}

// "Revisar com IA": what the backend returns for a document review
interface AiReviewData {
  summary: string;
  docType: string;
  parties: { name: string; role: string }[];
  values: { label: string; value: string }[];
  dates: { label: string; value: string }[];
  penalties: string[];
  attentionPoints: { severity: "high" | "medium" | "low"; title: string; detail: string }[];
  missing: string[];
  truncated: boolean;
}

interface AiReviewStatus {
  enabled: boolean;
  allowed: boolean;
  unlimited: boolean;
  limit: number;
  used: number;
  remaining: number;
  reason?: string | null;
}

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function DocumentWizard() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [step, setStep] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showGovBrDialog, setShowGovBrDialog] = useState(false);
  const [selectedSignatureMethod, setSelectedSignatureMethod] = useState<"draw" | "govbr">("draw");
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  
  const [tempUpload, setTempUpload] = useState<TempUpload | null>(null);
  const [signers, setSigners] = useState<Signer[]>([{ 
    name: "", 
    email: "", 
    cpf: "", 
    phone: "", 
    phoneCountry: "BR", 
    sendWhatsApp: false,
    // By default place the signature on a single page rather than every page. Using 'both'
    // (all pages + main) caused signatures to appear on every page when sending, so the
    // sensible default for new signers is 'main'.
    signatureMode: 'main',
    positionSet: false
  }]);
  const [message, setMessage] = useState("");
  const [positionModalOpen, setPositionModalOpen] = useState(false);
  const [positioningSignerIndex, setPositioningSignerIndex] = useState<number | null>(null);
  const [tempPosition, setTempPosition] = useState({ x: 50, y: 82, page: 0 });
  const [pdfPageCount, setPdfPageCount] = useState(1);
  const [previewPage, setPreviewPage] = useState(0);
  const [signaturePlaced, setSignaturePlaced] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showPlacementAnimation, setShowPlacementAnimation] = useState(false);
  const positionContainerRef = useRef<HTMLDivElement>(null);
  
  const { user } = useAuth();

  // Monthly plan-limit info (drives the upgrade banner on step 1)
  const [limitInfo, setLimitInfo] = useState<{
    canUpload: boolean; currentCount: number; limit: number;
    messagePt?: string; messageEn?: string;
  } | null>(null);
  const [limitCheckFailed, setLimitCheckFailed] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(getApiUrl("/api/documents/check-limit"), { headers: getAuthHeaders() });
        if (res.ok) setLimitInfo(await res.json());
        else setLimitCheckFailed(true);
      } catch { setLimitCheckFailed(true); /* fail open: the API refuses the upload itself */ }
    })();
  }, []);
  const userPlan = user?.plan ?? "free";
  
  // AI positioning state
  const [aiLoading, setAiLoading] = useState(false);
  const [aiPreviewOpen, setAiPreviewOpen] = useState(false);
  const [aiPositions, setAiPositions] = useState<{signerIndex: number; name: string; x: number; y: number; page: number; confidence: string; found: boolean; message?: string}[]>([]);
  const [aiPageCount, setAiPageCount] = useState(1);
  const [aiPreviewPage, setAiPreviewPage] = useState(0);
  const aiAbortRef = useRef(false);

  // "Revisar com IA" (document review before sending)
  const [aiReview, setAiReview] = useState<AiReviewData | null>(null);
  const [aiReviewLoading, setAiReviewLoading] = useState(false);
  const [aiReviewStatus, setAiReviewStatus] = useState<AiReviewStatus | null>(null);

  const hasUnsavedChanges = tempUpload !== null;

  // Fetch company data to check country and CPF requirements
  const { data: companyData } = useQuery<any>({
    queryKey: ["/api/companies/my"],
  });

  const isCompanyBrazilian = companyData ? isBrazil(companyData.country || "") : false;
  // CPF is only required for normal signature method in Brazilian companies, not for GOV.BR
  const requiresCpf = (companyData?.requireCpf || isCompanyBrazilian) && selectedSignatureMethod !== "govbr";

  // CPF mask function - formats as 000.000.000-00
  const formatCpf = (value: string): string => {
    const digits = value.replace(/\D/g, "").slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
  };

  useEffect(() => {
    if (!localStorage.getItem("token")) {
      setLocation("/login");
    }
  }, [setLocation]);

  // Warn user before leaving if there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    };
    
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleNavigation = useCallback((path: string) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(path);
      setShowLeaveDialog(true);
    } else {
      setLocation(path);
    }
  }, [hasUnsavedChanges, setLocation]);

  const confirmLeave = () => {
    setShowLeaveDialog(false);
    if (pendingNavigation) {
      setLocation(pendingNavigation);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      // Use full API route including /api prefix so requests go to backend instead of the Vite server
      const res = await fetch(getApiUrl("/api/documents/temp-upload"), {
        method: "POST",
        headers: getAuthHeaders(),
        body: formData,
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Upload failed");
      }
      
      const result = await res.json();
      // If the backend wraps the response in a `data` property (e.g., { success, data: {...} }),
      // extract it, otherwise use the root object (Node.js API returns fields at root level).
      const uploadData = (result && typeof result === 'object' && 'data' in result ? (result as any).data : result) as TempUpload;
      // Ensure fileSize is a number to avoid NaN issues when dividing
      if (uploadData && typeof (uploadData as any).fileSize !== 'undefined') {
        (uploadData as any).fileSize = Number((uploadData as any).fileSize);
      }
      setTempUpload(uploadData);
      
      // RESET all signer positions when document is replaced
      // This forces users to re-select positions for the new document
      setSigners(prevSigners => prevSigners.map(s => ({
        ...s,
        positionSet: false,
        signaturePositionX: undefined,
        signaturePositionY: undefined,
        signaturePageNumber: undefined
      })));
      
      // Get PDF page count from backend
      if (uploadData.fileUrl) {
        try {
          const pageCountRes = await fetch(getApiUrl(`/api/documents/page-count?url=${encodeURIComponent(uploadData.fileUrl)}`), {
            headers: getAuthHeaders(),
          });
          if (pageCountRes.ok) {
            const pageData = await pageCountRes.json();
            const pages = pageData.pageCount || pageData.data?.pageCount || 1;
            setPdfPageCount(pages);
          }
        } catch (e) {
          console.log("Could not get page count, using default");
          setPdfPageCount(1);
        }
      }
      
      // Only show GOV.BR dialog if company is Brazilian
      if (isCompanyBrazilian) {
        setShowGovBrDialog(true);
      } else {
        // Move directly to preview step for non-Brazilian companies
        setStep(2);
      }
      toast({ title: t("wizard.uploaded") });
    } catch (error: any) {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const addSigner = () => {
    const defaultCountry = isCompanyBrazilian ? "BR" : "";
    setSigners([...signers, { 
      name: "", 
      email: "", 
      cpf: "", 
      phone: "", 
      phoneCountry: defaultCountry, 
      sendWhatsApp: false,
      // Default to 'main' to avoid unintentionally applying signatures to all pages.  Users
      // can still select other modes in the UI if desired.
      signatureMode: 'main',
      positionSet: false
    }]);
  };

  const removeSigner = (index: number) => {
    if (signers.length > 1) {
      setSigners(signers.filter((_, i) => i !== index));
    }
  };

  const updateSigner = (index: number, field: keyof Signer, value: string | boolean | number) => {
    const updated = [...signers];
    // Apply CPF mask for CPF field
    if (field === "cpf" && typeof value === "string") {
      if (updated[index].cpfLocked) return;   // the client's registered CPF
      updated[index][field] = formatCpf(value);
    } else {
      (updated[index] as any)[field] = value;
    }
    // Another e-mail than the client's: it is someone else, so the client's CPF no longer applies.
    if (field === "email" && typeof value === "string" && updated[index].contactId
        && !(updated[index].contactEmails || []).some((o) => o.email === value.trim().toLowerCase())) {
      const { contactId: _c, contactEmails: _e, cpfLocked, ...rest } = updated[index];
      updated[index] = cpfLocked ? { ...rest, cpf: "" } : rest;
    }
    setSigners(updated);
  };

  // Format phone number input with mask (DDD) xxxxx-xxxx for Brazilian numbers. Other numbers are left unchanged.
  const formatPhone = (value: string) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, "");
    // If there are less than 2 digits, just prefix with '('
    if (digits.length <= 2) return `(${digits}`;
    // DDD (area code) + first block (up to 5 digits) + optional last block (up to 4 digits)
    const area = digits.slice(0, 2);
    const first = digits.slice(2, 7);
    const second = digits.slice(7, 11);
    let result = `(${area})`;
    if (first) result += ` ${first}`;
    if (second) result += `-${second}`;
    return result;
  };

  // Fill a signer from a saved contact (lookup pick, or "Send document" from the contact record).
  // The picked e-mail (or the client's default one) is used; a registered CPF comes locked.
  const applyContactToSigner = (index: number, c: LookupContact, pickedEmail?: string, onlyIfEmpty = false) => {
    setSigners((prev) => {
      if (!prev[index]) return prev;
      const current = prev[index];
      if (onlyIfEmpty && (current.name.trim() || current.email.trim())) return prev;
      const phone = splitPhone(c.phone);
      const emails = contactEmails(c);
      const cpf = c.cpf ? formatCpf(c.cpf) : "";
      const next = [...prev];
      next[index] = {
        ...current,
        name: c.name || current.name,
        email: pickedEmail || emails.find((e) => e.isDefault)?.email || c.email || current.email,
        contactId: c.id,
        contactEmails: emails,
        ...(cpf ? { cpf, cpfLocked: true } : current.cpfLocked ? { cpf: "", cpfLocked: false } : {}),
        ...(phone
          ? { phone: phone.country === "BR" ? formatPhone(phone.national) : phone.national, phoneCountry: phone.country || current.phoneCountry }
          : {}),
      };
      return next;
    });
  };

  // /documents/new?contactId=… or ?leadId=… (from the CRM): the signer comes already filled
  // and the sent document is linked to that record (timeline + Files tab).
  const search = useSearch();
  const crmSource = useMemo(() => {
    const params = new URLSearchParams(search);
    const contactId = params.get("contactId");
    const leadId = params.get("leadId");
    if (contactId) return { entity: "contact" as const, id: contactId };
    if (leadId) return { entity: "lead" as const, id: leadId };
    return null;
  }, [search]);
  const prefilledFrom = useRef<string | null>(null);
  useEffect(() => {
    if (!crmSource || prefilledFrom.current === crmSource.id) return;
    prefilledFrom.current = crmSource.id;
    (async () => {
      try {
        const fromContact = async (contactId: string) => {
          const res = await fetch(getApiUrl(`/api/contacts/${encodeURIComponent(contactId)}`), { headers: getAuthHeaders() });
          if (!res.ok) return false;
          applyContactToSigner(0, mapLookupContact(await res.json()), undefined, true);
          return true;
        };
        if (crmSource.entity === "contact") {
          await fromContact(crmSource.id);
        } else {
          // A lead belongs to a client: the signer is the client (both e-mails, CPF).
          const res = await fetch(getApiUrl(`/api/crm/leads/${encodeURIComponent(crmSource.id)}`), { headers: getAuthHeaders() });
          if (!res.ok) return;
          const v = (await res.json())?.values || {};
          if (v.contactId && await fromContact(String(v.contactId))) return;
          applyContactToSigner(0, { id: "", name: v.fullName || "", email: v.email || "", phone: v.mobilePhone }, undefined, true);
        }
      } catch { /* the user can still type the signer */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crmSource]);

  // /documents/new?fromModel=1: the PDF generated from a library model is already stored; start at the review step.
  const modelTaken = useRef(false);
  useEffect(() => {
    if (modelTaken.current || !new URLSearchParams(search).get("fromModel")) return;
    modelTaken.current = true;
    let pending: any = null;
    try { pending = JSON.parse(sessionStorage.getItem(PENDING_UPLOAD_KEY) || "null"); } catch { pending = null; }
    if (!pending?.fileUrl) return;
    sessionStorage.removeItem(PENDING_UPLOAD_KEY);
    setTempUpload({ tempUploadId: pending.tempUploadId, name: pending.name, type: pending.type || "pdf", fileUrl: pending.fileUrl, previewUrl: pending.previewUrl || pending.fileUrl, fileSize: Number(pending.fileSize) || 0 });
    setPdfPageCount(Number(pending.pageCount) || 1);
    setStep(2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const isSignersValid = () => {
    const basicValid = signers.every(s => s.name.trim() && s.email.trim() && s.email.includes("@"));
    const positionValid = signers.every(s => s.positionSet === true);
    if (requiresCpf) {
      return basicValid && positionValid && signers.every(s => s.cpf && s.cpf.trim().length >= 11);
    }
    return basicValid && positionValid;
  };

  const openPositionModal = (index: number) => {
    setPositioningSignerIndex(index);
    const signer = signers[index];
    // Default to last page if position not set, otherwise use saved position
    const defaultPage = signer.positionSet ? (signer.signaturePageNumber || 0) : Math.max(0, pdfPageCount - 1);
    setTempPosition({
      x: signer.signaturePositionX ?? 50, // Center (50%)
      y: signer.signaturePositionY ?? 82, // Near bottom (82%)
      page: defaultPage
    });
    setSignaturePlaced(signer.positionSet || false);
    setShowPlacementAnimation(false);
    setPositionModalOpen(true);
  };

  const savePosition = () => {
    if (positioningSignerIndex === null) return;
    const updated = [...signers];
    updated[positioningSignerIndex] = {
      ...updated[positioningSignerIndex],
      signaturePositionX: tempPosition.x,
      signaturePositionY: tempPosition.y,
      signaturePageNumber: tempPosition.page,
      positionSet: true
    };
    setSigners(updated);
    setPositionModalOpen(false);
    setPositioningSignerIndex(null);
  };

  const analyzeWithAi = async () => {
    if (!tempUpload) return;
    setAiLoading(true);
    aiAbortRef.current = false;
    try {
      const res = await fetch(getApiUrl("/api/documents/analyze-positions"), {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          tempUploadId: tempUpload.tempUploadId,
          fileUrl: tempUpload.fileUrl,
          fileName: tempUpload.name,
          signers: signers.map(s => ({ name: s.name, email: s.email }))
        })
      });
      if (aiAbortRef.current) return;
      const data = await res.json();
      if (data.success === false && data.errorCode === "PLAN_LIMIT") {
        toast({
          title: language === "pt" ? "Limite do plano" : "Plan limit",
          description: language === "pt"
            ? "O posicionamento por IA acabou junto com os documentos do mês no plano gratuito. Posicione a assinatura manualmente ou faça upgrade."
            : "AI positioning is used up with this month's documents on the Free plan. Place the signature manually or upgrade.",
          variant: "destructive",
        });
        return;
      }
      if (!res.ok) {
        toast({ title: t("common.error"), description: data.message || "AI analysis failed", variant: "destructive" });
        return;
      }
      const positions = data.positions ? [...data.positions] : [];
      setAiPositions(positions);
      setAiPageCount(data.pageCount || 1);
      setAiPreviewPage(positions.length > 0 ? positions[0].page : 0);
      setAiPreviewOpen(true);
    } catch (err: any) {
      if (!aiAbortRef.current) {
        toast({ title: t("common.error"), description: err.message || "AI analysis failed", variant: "destructive" });
      }
    } finally {
      setAiLoading(false);
    }
  };

  // ── Revisar com IA: the document review shown at the review step ──
  useEffect(() => { setAiReview(null); }, [tempUpload?.tempUploadId]);

  useEffect(() => {
    if (step !== 4 || aiReviewStatus !== null) return;
    (async () => {
      try {
        const res = await fetch(getApiUrl("/api/documents/ai-review/status"), { headers: getAuthHeaders() });
        const data = await res.json();
        if (res.ok && data.status) setAiReviewStatus(data.status);
      } catch { /* the button still works; the server answers either way */ }
    })();
  }, [step, aiReviewStatus]);

  const runAiReview = async () => {
    if (!tempUpload || aiReviewLoading) return;
    setAiReviewLoading(true);
    try {
      const res = await fetch(getApiUrl("/api/documents/ai-review"), {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          tempUploadId: tempUpload.tempUploadId,
          fileUrl: tempUpload.fileUrl,
          fileName: tempUpload.name,
          language
        })
      });
      const data = await res.json();
      if (!res.ok) {
        toast({ title: t("common.error"), description: data.message || t("wizard.aiReviewError"), variant: "destructive" });
        return;
      }
      if (data.status) setAiReviewStatus(data.status);
      if (data.success === false) {
        toast({
          title: data.errorCode === "PLAN_LIMIT" ? (language === "pt" ? "Limite do plano" : "Plan limit") : t("common.error"),
          description: (language === "pt" ? data.messagePt : data.messageEn) || t("wizard.aiReviewError"),
          variant: "destructive",
        });
        return;
      }
      setAiReview(data.review);
    } catch (err: any) {
      toast({ title: t("common.error"), description: err.message || t("wizard.aiReviewError"), variant: "destructive" });
    } finally {
      setAiReviewLoading(false);
    }
  };

  const aiReviewSeverityClass = (severity: string) =>
    severity === "high"
      ? "text-red-700 border-red-300 bg-red-50 dark:bg-red-900/20 dark:text-red-400"
      : severity === "low"
        ? "text-muted-foreground border-muted"
        : "text-amber-700 border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-400";

  // Like the PCF: advancing to review runs the AI for any signer without a position, previews, then continues on confirm.
  const proceedAfterAiRef = useRef(false);
  const goToReview = () => {
    const basicValid = signers.every(s => s.name.trim() && s.email.trim() && s.email.includes("@"));
    if (!basicValid) return;
    if (signers.every(s => s.positionSet === true)) { goToStep(4); return; }
    proceedAfterAiRef.current = true;
    analyzeWithAi();
  };

  const confirmAiPositions = () => {
    const updated = [...signers];
    for (const pos of aiPositions) {
      if (pos.signerIndex < updated.length) {
        updated[pos.signerIndex] = {
          ...updated[pos.signerIndex],
          signaturePositionX: pos.x,
          signaturePositionY: pos.y,
          signaturePageNumber: pos.page,
          positionSet: true
        };
      }
    }
    setSigners(updated);
    setAiPreviewOpen(false);
    toast({ title: t("wizard.aiConfirmedTitle"), description: `${aiPositions.length} ${t("wizard.aiConfirmedDesc")}` });
    if (proceedAfterAiRef.current) { proceedAfterAiRef.current = false; setTimeout(() => goToStep(4), 50); }
  };

  const adjustAiManually = () => {
    // Apply AI positions as starting point, then let user adjust manually
    const updated = [...signers];
    for (const pos of aiPositions) {
      if (pos.signerIndex < updated.length) {
        updated[pos.signerIndex] = {
          ...updated[pos.signerIndex],
          signaturePositionX: pos.x,
          signaturePositionY: pos.y,
          signaturePageNumber: pos.page,
          positionSet: true
        };
      }
    }
    setSigners(updated);
    setAiPreviewOpen(false);
  };

  const handleSend = async () => {
    if (!tempUpload) return;
    
    setSending(true);
    try {
      // Format phone numbers with country code and include position data
      const formattedSigners = signers.map(s => {
        let fullPhone = s.phone;
        if (s.sendWhatsApp && s.phone && s.phoneCountry) {
          const countryCode = getPhoneCode(s.phoneCountry);
          // Remove any leading zeros and non-digit characters (spaces, dashes, parentheses)
          const cleanPhone = s.phone.replace(/^0+/, "").replace(/\D/g, "");
          fullPhone = `${countryCode}${cleanPhone}`;
        }
        return { 
          name: s.name, 
          email: s.email, 
          cpf: s.cpf, 
          phone: fullPhone,
          sendWhatsApp: s.sendWhatsApp,
          // Include position data defined by sender
          signaturePositionX: s.signaturePositionX,
          signaturePositionY: s.signaturePositionY,
          signaturePageNumber: s.signaturePageNumber,
          // Ensure the API receives the correct signatureMode. Default to 'main' when
          // unspecified. Using 'both' by default leads to signatures on all pages.
          signatureMode: s.signatureMode || 'main'
        };
      });
      
      // Use full API route including /api prefix so requests go to backend instead of the Vite server
      const res = await fetch(getApiUrl("/api/documents/finalize"), {
        method: "POST",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          tempUploadId: tempUpload.tempUploadId,
          name: tempUpload.name,
          type: tempUpload.type,
          fileUrl: tempUpload.fileUrl,
          previewUrl: tempUpload.previewUrl,
          fileSize: tempUpload.fileSize,
          signers: formattedSigners,
          signatureMethod: selectedSignatureMethod,
          message,
          ...(crmSource ? { crmEntity: crmSource.entity, crmRecordId: crmSource.id } : {}),
        }),
      });
      
      if (!res.ok) {
        const data = await res.json();
        if (data.errorCode === "DOCUMENT_LIMIT_REACHED") {
          const msg = language === "pt"
            ? (data.messagePt || data.message || "Limite mensal de documentos atingido.")
            : (data.messageEn || data.message || "Monthly document limit reached.");
          toast({
            title: language === "pt" ? "Limite atingido" : "Limit reached",
            description: msg,
            variant: "destructive",
          });
          setLocation("/settings");
          return;
        }
        if (data.errorCode === "PAYMENT_METHOD_REQUIRED") {
          const msg = language === "pt"
            ? (data.messagePt || "Adicione um cartão para começar a enviar documentos.")
            : (data.messageEn || "Please add a payment method to start sending documents.");
          toast({
            title: language === "pt" ? "Cartão necessário" : "Card required",
            description: msg,
            variant: "destructive",
          });
          setLocation("/verify-payment");
          return;
        }
        throw new Error(data.message || "Failed to send");
      }
      
      // Refresh every screen that shows document state (list, dashboard, signatures, stats)
      invalidateDocumentQueries(queryClient);
      
      // Clear temp upload before navigation so beforeunload doesn't trigger
      setTempUpload(null);
      toast({
        title: language === "pt" ? "Documento enviado!" : "Signature requests sent!",
        description: language === "pt" ? `Enviado para ${signers.length} assinante(s)` : `Sent to ${signers.length} recipient(s)`,
      });
      if (crmSource) {
        // Back to the CRM record, whose timeline and Files tab now show the document.
        queryClient.invalidateQueries({ queryKey: ["crm"] });
        setLocation(crmSource.entity === "lead" ? `/leads/${crmSource.id}` : `/contacts/${crmSource.id}`);
      } else {
        setLocation("/signatures");
      }
    } catch (error: any) {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  /**
   * Build a URL that can be used in an <iframe> to preview the uploaded document.
   * Uses the API's download endpoint which handles file location resolution.
   */
  const getPreviewUrl = () => {
    if (!tempUpload) return '';
    const src = tempUpload.previewUrl || tempUpload.fileUrl;
    if (!src) return '';
    // If the URL is already absolute (starts with http), return it unchanged
    if (/^https?:\/\//i.test(src)) return src;
    // Use the download endpoint with inline mode for browser preview
    const encoded = encodeURIComponent(src);
    return getApiUrl(`/api/files/download?fileUrl=${encoded}&inline=true`) + '#toolbar=0&navpanes=0&view=FitH';
  };

  const { t, language } = useLanguage();
  
  const steps = [
    { num: 1, label: t("wizard.upload"), icon: Upload },
    { num: 2, label: t("wizard.preview"), icon: Eye },
    { num: 3, label: t("wizard.signers"), icon: Users },
    { num: 4, label: t("wizard.review"), icon: Check },
  ];

  // Track direction for slide animation
  const [slideDirection, setSlideDirection] = useState<"left" | "right">("right");

  const goToStep = (newStep: number) => {
    setSlideDirection(newStep > step ? "right" : "left");
    setStep(newStep);
    
    // When going to review step, initialize preview page to show first signer's position
    if (newStep === 4 && signers.length > 0) {
      const firstSignerPage = signers[0].signaturePageNumber || 0;
      setPreviewPage(firstSignerPage);
    }
  };

  const tourMarks = [
    { selector: '[data-tour="upload"]', title: t("tour.w1Title"), text: t("tour.w1Text") },
    { selector: '[data-testid="button-next-signers"]', title: t("tour.w2Title"), text: t("tour.w2Text") },
    { selector: '[data-testid="input-signer-name-0"]', title: t("tour.w3Title"), text: t("tour.w3Text") },
    { selector: '[data-tour="whatsapp"]', title: t("tour.w4Title"), text: t("tour.w4Text") },
    { selector: '[data-testid="button-add-signer"]', title: t("tour.w5Title"), text: t("tour.w5Text") },
    { selector: '[data-testid="button-next-review"]', title: t("tour.w6Title"), text: t("tour.w6Text") },
    { selector: '[data-testid="button-send"]', title: t("tour.w7Title"), text: t("tour.w7Text") },
  ];

  const limitBlocked = limitInfo !== null && limitInfo.limit !== -1 && !limitInfo.canUpload;
  const limitPending = limitInfo === null && !limitCheckFailed;

  return (
    <div className="min-h-screen bg-background">
      {!limitBlocked && !limitPending && <CoachMarks storageKey="fs:tour:wizard:v1" marks={tourMarks} active={true} />}
      <header className="sticky top-0 z-50 border-b bg-background">
        <div className="mx-auto max-w-4xl px-6 py-4 flex items-center justify-between gap-4">
          <button 
            onClick={() => handleNavigation("/documents")} 
            className="flex items-center gap-2 text-sm hover-elevate px-2 py-1 rounded-md"
            data-testid="button-back-documents"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>{t("common.back")} {t("nav.documents")}</span>
          </button>
          <div className="flex items-center gap-2">
            <BrandIcon size="sm" />
            <span className="font-semibold">{t("wizard.newDocument") || "New Document"}</span>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-8">
        {limitBlocked ? (
          <DocumentLimitReached onBack={() => setLocation("/documents")} />
        ) : limitPending ? (
          <div className="flex justify-center py-24" data-testid="wizard-limit-checking"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
        ) : (
        <>
        {/* Animated Timeline with smooth transitions */}
        <div className="flex items-center justify-center gap-2 mb-8 overflow-hidden px-4">
          {steps.map((s, i) => (
            <div key={s.num} className="flex items-center">
              <div 
                className={cn(
                  "flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all duration-500 ease-out transform cursor-pointer select-none",
                  step >= s.num 
                    ? "bg-gradient-to-r from-primary to-primary/80 text-primary-foreground shadow-lg shadow-primary/25" 
                    : "bg-muted/50 text-muted-foreground hover:bg-muted",
                  step === s.num && "scale-105 ring-2 ring-primary/30 ring-offset-2 ring-offset-background",
                  step < s.num && "opacity-60 scale-95"
                )}
                style={{
                  transform: step === s.num ? 'translateY(-2px)' : 'translateY(0)',
                  transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
                }}
              >
                <div className={cn(
                  "relative",
                  step === s.num && "animate-pulse"
                )}>
                  <s.icon className={cn(
                    "h-4 w-4 transition-all duration-500",
                    step === s.num && "scale-110"
                  )} />
                  {step > s.num && (
                    <Check className="absolute -bottom-1 -right-1 h-3 w-3 text-green-400 animate-in zoom-in-50 duration-300" />
                  )}
                </div>
                <span className={cn(
                  "hidden sm:inline text-sm font-medium transition-all duration-300",
                  step === s.num && "font-semibold"
                )}>{s.label}</span>
              </div>
              {i < steps.length - 1 && (
                <div className="relative w-12 h-1 mx-2">
                  <div className="absolute inset-0 bg-muted rounded-full" />
                  <div 
                    className={cn(
                      "absolute inset-y-0 left-0 bg-gradient-to-r from-primary to-primary/70 rounded-full transition-all duration-700 ease-out"
                    )}
                    style={{
                      width: step > s.num ? '100%' : '0%'
                    }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Content with smooth slide animation */}
        <div 
          key={step}
          className={cn(
            "transition-all duration-500 ease-out",
            "animate-in fade-in-0 duration-500",
            slideDirection === "right" 
              ? "slide-in-from-right-8" 
              : "slide-in-from-left-8"
          )}
          style={{
            animationTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)'
          }}
        >
        {step === 1 && (
          <Card className="animate-in fade-in-0 zoom-in-95 duration-500 ease-out overflow-hidden">
            {limitInfo && limitInfo.limit !== -1 && (limitInfo.limit - limitInfo.currentCount) <= 1 && (
              <div
                role="button"
                onClick={() => setLocation("/settings")}
                className={`px-4 py-3 text-sm font-medium cursor-pointer flex items-center justify-between gap-3 ${limitInfo.canUpload ? "bg-amber-100 text-amber-900 border-b border-amber-300" : "bg-red-100 text-red-900 border-b border-red-300"}`}
                data-testid="banner-document-limit"
              >
                <span>
                  {limitInfo.canUpload
                    ? (language === "pt"
                        ? `Este é o seu último documento do mês (${limitInfo.currentCount + 1}/${limitInfo.limit}). Clique para fazer upgrade do plano.`
                        : `This is your last document this month (${limitInfo.currentCount + 1}/${limitInfo.limit}). Click to upgrade your plan.`)
                    : (language === "pt"
                        ? (limitInfo.messagePt || `Você atingiu o limite mensal de ${limitInfo.limit} documentos. Clique para fazer upgrade.`)
                        : (limitInfo.messageEn || `You have reached your monthly limit of ${limitInfo.limit} documents. Click to upgrade.`))}
                </span>
                <span className="underline whitespace-nowrap">{language === "pt" ? "Ver planos →" : "View plans →"}</span>
              </div>
            )}
            <CardHeader className="text-center space-y-2">
              <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-2">
                <Upload className="h-8 w-8 text-primary" />
              </div>
              <CardTitle className="text-2xl">{t("wizard.uploadTitle")}</CardTitle>
              <CardDescription className="text-base">{t("wizard.uploadDesc")}</CardDescription>
            </CardHeader>
            <CardContent data-tour="upload">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="input-file"
              />
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-muted-foreground/25 rounded-xl p-16 text-center cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-all duration-300 group"
              >
                {uploading ? (
                  <div className="flex flex-col items-center gap-4">
                    <Loader2 className="h-12 w-12 animate-spin text-primary" />
                    <p className="text-muted-foreground">{t("common.loading")}</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-4">
                    <Upload className="h-12 w-12 text-muted-foreground group-hover:text-primary group-hover:scale-110 transition-all duration-300" />
                    <div>
                      <p className="font-medium">{t("wizard.clickToSelect") || "Click to select a file"}</p>
                      <p className="text-sm text-muted-foreground">{t("wizard.uploadFormats") || "PDF or Word documents up to 10MB"}</p>
                    </div>
                  </div>
                )}
              </div>
              <Link href="/modelos" className="mt-4 flex items-center gap-3 rounded-xl border bg-muted/40 p-4 transition hover:border-primary/60 hover:bg-primary/5" data-testid="wizard-use-model">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10"><BookOpen className="h-5 w-5 text-primary" /></div>
                <div className="min-w-0">
                  <p className="font-medium">{language === "pt" ? "Ou use um modelo da biblioteca" : "Or use a model from the library"}</p>
                  <p className="text-sm text-muted-foreground">{language === "pt" ? "Contratos brasileiros prontos: preencha os campos e siga para a assinatura." : "Ready Brazilian contracts (in Portuguese): fill in the fields and continue to signature."}</p>
                </div>
                <ArrowRight className="ml-auto h-4 w-4 shrink-0 text-muted-foreground" />
              </Link>
            </CardContent>
          </Card>
        )}

        {step === 2 && tempUpload && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <FileText className="h-6 w-6 text-primary" />
                {tempUpload.name}
              </CardTitle>
              <CardDescription>{t("wizard.reviewDocument") || "Review your document before adding signers"}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-muted rounded-lg p-4 min-h-[400px] flex flex-col">
                {tempUpload.type === "application/pdf" || tempUpload.name.toLowerCase().endsWith(".pdf") ? (
                  <PdfPreview src={getPreviewUrl().split('#')[0]} className="w-full" maxHeight={500} />
                ) : (
                  <div className="flex flex-col items-center justify-center h-[500px] gap-4">
                    <FileText className="h-16 w-16 text-primary" />
                    <div className="text-center">
                      <p className="font-medium">{tempUpload.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {(tempUpload.fileSize / 1024).toFixed(1)} KB - {tempUpload.type}
                      </p>
                    </div>
                    <Button variant="outline" onClick={() => window.open(getPreviewUrl(), '_blank')}>
                      <Download className="mr-2 h-4 w-4" /> {t("wizard.downloadToView")}
                    </Button>
                  </div>
                )}
              </div>
              <div className="flex justify-between gap-4">
                <Button variant="outline" onClick={() => goToStep(1)}>
                  <ArrowLeft className="mr-2 h-4 w-4" /> {t("common.back")}
                </Button>
                <Button onClick={() => goToStep(3)} data-testid="button-next-signers">
                  {t("common.next")}: {t("wizard.addSigners")} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 3 && (
          <Card className="animate-in fade-in-50 zoom-in-95 duration-300">
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <Users className="h-6 w-6 text-primary" />
                {t("wizard.addSigners")}
              </CardTitle>
              <CardDescription>{t("ui.enter_the_name_and_email_of_each_person_who_need")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* AI Positioning Button */}
              {tempUpload && signers.length > 0 && signers.every(s => s.name.trim() && s.email.trim()) && (
                <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <Bot className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-100">{t("wizard.aiBannerTitle")}</p>
                    <p className="text-xs text-blue-600 dark:text-blue-400">{t("ui.automatically_detect_the_best_position_for_each")}</p>
                  </div>
                  <Button
                    size="sm"
                    disabled={aiLoading}
                    onClick={analyzeWithAi}
                    className="flex-shrink-0"
                  >
                    {aiLoading ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t("ui.analyzing")}</>
                    ) : (
                      <><Bot className="h-4 w-4 mr-2" /> {t("wizard.aiPositionAll")}</>
                    )}
                  </Button>
                  {aiLoading && (
                    <Button size="sm" variant="ghost" onClick={() => { aiAbortRef.current = true; setAiLoading(false); }}>
                      {t("ui.cancel")}
                    </Button>
                  )}
                </div>
              )}

              <div className="space-y-4">
                {signers.map((signer, index) => (
                  <div key={index} className="relative rounded-lg border border-l-4 p-4 pt-5 space-y-4" style={{ borderLeftColor: ["#4A6D2E", "#1D4ED8", "#B45309", "#7C3AED", "#0F766E"][index % 5] }} data-testid={`signer-card-${index}`}>
                    <div className="flex items-center gap-2 text-sm font-semibold">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full text-[11px] text-white" style={{ background: ["#4A6D2E", "#1D4ED8", "#B45309", "#7C3AED", "#0F766E"][index % 5] }}>{index + 1}</span>
                      {t("wizard.signerN")} {index + 1}{signer.name.trim() ? ` · ${signer.name.trim()}` : ""}
                    </div>
                    <div className={`grid gap-4 ${isCompanyBrazilian ? "sm:grid-cols-2 lg:grid-cols-3" : "sm:grid-cols-2"}`}>
                      <div className="space-y-2">
                        <Label>{t("ui.name")}</Label>
                        <ContactLookupInput
                          placeholder={language === "pt" ? "Nome completo" : "Full name"}
                          value={signer.name}
                          onChange={(v) => updateSigner(index, "name", v)}
                          onPick={(c, email) => applyContactToSigner(index, c, email)}
                          data-testid={`input-signer-name-${index}`}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t("ui.email")}</Label>
                        <ContactLookupInput
                          type="email"
                          placeholder={language === "pt" ? "nome@empresa.com.br" : "name@company.com"}
                          value={signer.email}
                          onChange={(v) => updateSigner(index, "email", v)}
                          onPick={(c, email) => applyContactToSigner(index, c, email)}
                          data-testid={`input-signer-email-${index}`}
                        />
                        {signer.contactEmails && signer.contactEmails.length > 1 && (
                          <div className="flex flex-wrap gap-1.5" data-testid={`signer-email-options-${index}`}>
                            {signer.contactEmails.map((o) => (
                              <button
                                key={o.email}
                                type="button"
                                onClick={() => updateSigner(index, "email", o.email)}
                                className={cn(
                                  "inline-flex max-w-full items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]",
                                  signer.email.trim().toLowerCase() === o.email ? "border-primary bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground",
                                )}
                                title={o.isDefault ? t("wizard.emailDefault") : undefined}
                                data-testid={`signer-email-option-${index}-${o.kind}`}
                              >
                                {o.isDefault && <Star className="h-3 w-3 shrink-0 fill-current text-amber-500" />}
                                <span className="shrink-0">{o.kind === "personal" ? t("wizard.emailPersonal") : t("wizard.emailWork")}:</span>
                                <span className="truncate">{o.email}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      {isCompanyBrazilian && (
                        <div className="space-y-2">
                          <Label>CPF {selectedSignatureMethod === "govbr" && <span className="text-muted-foreground font-normal">({t("wizard.optional")})</span>}</Label>
                          <div className="relative">
                            <Input
                              placeholder="000.000.000-00"
                              value={signer.cpf || ""}
                              readOnly={signer.cpfLocked}
                              className={signer.cpfLocked ? "bg-muted/40 pr-8" : undefined}
                              onChange={(e) => updateSigner(index, "cpf", e.target.value)}
                              data-testid={`input-signer-cpf-${index}`}
                            />
                            {signer.cpfLocked && <Lock className="pointer-events-none absolute right-2.5 top-2.5 h-4 w-4 text-muted-foreground" data-testid={`signer-cpf-locked-${index}`} />}
                          </div>
                          {signer.cpfLocked && <p className="text-xs text-muted-foreground">{t("wizard.cpfFromContact")}</p>}
                        </div>
                      )}
                    </div>
                    
                    {/* WhatsApp notification option */}
                    <div className="flex items-start gap-4 p-3 bg-green-500/5 rounded-lg border border-green-500/20" data-tour={index === 0 ? "whatsapp" : undefined}>
                      <Checkbox
                        id={`whatsapp-${index}`}
                        checked={signer.sendWhatsApp || false}
                        onCheckedChange={(checked) => updateSigner(index, "sendWhatsApp", checked)}
                      />
                      <div className="flex-1 grid gap-3">
                        <label htmlFor={`whatsapp-${index}`} className="flex items-center gap-2 cursor-pointer">
                          <WhatsAppIcon />
                          <span className="text-sm font-medium text-green-700 dark:text-green-400">
                            {t("ui.also_send_via_whatsapp")}
                          </span>
                        </label>
                        {signer.sendWhatsApp && (
                          <div className="space-y-2">
                            <div className="flex gap-2 items-start">
                              <Select
                                // Inherit the phone country from the first signer if not explicitly set.
                                // If the first signer has selected a country, subsequent signers will default to that
                                // value instead of an empty string.  This prevents the select from appearing blank.
                                value={signer.phoneCountry || (signers.length > 0 ? signers[0]?.phoneCountry : undefined) || (isCompanyBrazilian ? "BR" : "")}
                                onValueChange={(value) => updateSigner(index, "phoneCountry", value)}
                              >
                                <SelectTrigger className="w-[140px]">
                                  <SelectValue placeholder={t("ui.country")} />
                                </SelectTrigger>
                                <SelectContent className="max-h-[300px]">
                                  {countries.filter(c => c.phoneCode).map((country) => (
                                    <SelectItem key={country.code} value={country.code}>
                                      {country.phoneCode} {country.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Input
                                type="tel"
                                placeholder="(DD) xxxxx-xxxx"
                                value={signer.phone || ""}
                                onChange={(e) => updateSigner(index, "phone", formatPhone(e.target.value))}
                                className="flex-1 max-w-[200px]"
                                data-testid={`input-signer-phone-${index}`}
                              />
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Select country code and enter phone number (without country code)
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Signature Position Section - Required */}
                    <div className="p-4 bg-primary/5 rounded-lg border border-primary/20 space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Pen className="h-4 w-4 text-primary" />
                          <span className="font-medium text-sm">{t("wizard.signaturePosition") || "Signature Position"}</span>
                          <span className="text-destructive">*</span>
                        </div>
                        {signer.positionSet ? (
                          <Badge variant="secondary" className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                            <Check className="h-3 w-3 mr-1" /> {t("wizard.positionSet") || "Position Set"}
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
                            {t("wizard.positionRequired") || "Position Required"}
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex flex-col sm:flex-row gap-4">
                        <Button 
                          variant={signer.positionSet ? "outline" : "default"}
                          size="sm"
                          onClick={() => openPositionModal(index)}
                          className="flex-1"
                        >
                          <Pen className="h-4 w-4 mr-2" />
                          {signer.positionSet 
                            ? (t("wizard.changePosition") || "Change Position") 
                            : (t("wizard.setPosition") || "Set Signature Position")}
                        </Button>
                        
                        <Select
                          /*
                            Default signatureMode to 'main'. When signatureMode is undefined
                            (e.g. a new signer), falling back to 'main' prevents
                            unintentionally selecting 'both', which previously caused
                            signatures to appear on all pages as footers.
                          */
                          value={signer.signatureMode || 'main'}
                          onValueChange={(value: 'main' | 'footer' | 'both') => updateSigner(index, "signatureMode", value)}
                        >
                          <SelectTrigger className="w-full sm:w-[200px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="main">{t("wizard.mainOnly") || "Main Signature Only"}</SelectItem>
                            <SelectItem value="footer">{t("wizard.footerOnly") || "Footer Only (All Pages)"}</SelectItem>
                            <SelectItem value="both">{t("wizard.mainAndFooter") || "Main + Footer"}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {signer.positionSet && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Check className="h-3 w-3 text-green-600" />
                          {t("wizard.positionSetOnPage") || "Posição definida na"} {t("wizard.page") || "página"} {(signer.signaturePageNumber || 0) + 1}
                        </p>
                      )}
                    </div>
                    
                    {signers.length > 1 && (
                      <div className="flex justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeSigner(index)}
                          className="text-destructive hover:text-destructive"
                          data-testid={`button-remove-signer-${index}`}
                        >
                          <Trash2 className="h-4 w-4 mr-2" /> {t("ui.remove")}
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              <Button variant="outline" onClick={addSigner} className={signers.length === 1 ? "fs-pulse-8" : ""} data-testid="button-add-signer">
                <Plus className="mr-2 h-4 w-4" /> {t("ui.add_another_signer")}
              </Button>
              
              <div className="space-y-2">
                <Label>{t("ui.message_optional")}</Label>
                <Textarea
                  placeholder={t("ui.please_review_and_sign_this_document")}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="min-h-[100px]"
                  data-testid="input-message"
                />
              </div>
              
              <div className="flex justify-between gap-4">
                <Button variant="outline" onClick={() => goToStep(2)}>
                  <ArrowLeft className="mr-2 h-4 w-4" /> {t("common.back")}
                </Button>
                <Button onClick={goToReview} disabled={!signers.every(s => s.name.trim() && s.email.trim() && s.email.includes("@")) || aiLoading} data-testid="button-next-review">
                  {t("common.next")}: {t("wizard.review")} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 4 && tempUpload && (
          <Card className="animate-in fade-in-50 zoom-in-95 duration-300">
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <Check className="h-6 w-6 text-primary" />
                {t("wizard.reviewAndSend") || "Review & Send"}
              </CardTitle>
              <CardDescription>{t("wizard.confirmDetails") || "Confirm the details before sending"}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium mb-2">{t("nav.documents")}</h4>
                  <div className="flex items-center gap-3">
                    <FileText className="h-8 w-8 text-primary" />
                    <div>
                      <p className="font-medium">{tempUpload.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {(tempUpload.fileSize / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Revisar com IA: reading aid before sending (hidden only when the feature is off) */}
                {(aiReviewStatus === null || aiReviewStatus.enabled) && (
                  <div className="p-4 border rounded-lg" data-testid="ai-review-card">
                    <div className="flex items-start justify-between gap-3 mb-1">
                      <h4 className="font-medium flex items-center gap-2">
                        <Bot className="h-4 w-4 text-primary" />
                        {t("wizard.aiReviewTitle")}
                      </h4>
                      {aiReviewStatus && !aiReviewStatus.unlimited && aiReviewStatus.limit > 0 && (
                        <Badge variant="outline" className="text-xs shrink-0">
                          {t("wizard.aiReviewRemaining").replace("{0}", String(aiReviewStatus.remaining))}
                        </Badge>
                      )}
                    </div>
                    {!aiReview ? (
                      <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">{t("wizard.aiReviewAbout")}</p>
                        <Button
                          onClick={runAiReview}
                          disabled={aiReviewLoading}
                          variant="outline"
                          className="gap-2"
                          data-testid="button-ai-review"
                        >
                          {aiReviewLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bot className="h-4 w-4" />}
                          {aiReviewLoading ? t("wizard.aiReviewLoading") : t("wizard.aiReviewButton")}
                        </Button>
                        <p className="text-xs text-muted-foreground">{t("wizard.aiReviewPrivacy")}</p>
                      </div>
                    ) : (
                      <div className="space-y-4 mt-2" data-testid="ai-review-result">
                        <div>
                          {aiReview.docType && <Badge variant="secondary" className="mb-2">{aiReview.docType}</Badge>}
                          <p className="text-sm">{aiReview.summary}</p>
                        </div>
                        {aiReview.attentionPoints.length > 0 && (
                          <div className="space-y-2">
                            <h5 className="text-sm font-medium">{t("wizard.aiReviewAttention")}</h5>
                            {aiReview.attentionPoints.map((point, i) => (
                              <div key={i} className="flex items-start gap-2 text-sm">
                                <Badge variant="outline" className={cn("text-[10px] uppercase shrink-0 mt-0.5", aiReviewSeverityClass(point.severity))}>
                                  {point.severity === "high" ? t("wizard.aiReviewSevHigh") : point.severity === "low" ? t("wizard.aiReviewSevLow") : t("wizard.aiReviewSevMedium")}
                                </Badge>
                                <div>
                                  <span className="font-medium">{point.title}</span>
                                  {point.detail && <span className="text-muted-foreground"> {point.detail}</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="grid gap-4 sm:grid-cols-2">
                          {aiReview.parties.length > 0 && (
                            <div>
                              <h5 className="text-sm font-medium mb-1">{t("wizard.aiReviewParties")}</h5>
                              <ul className="text-sm text-muted-foreground space-y-0.5">
                                {aiReview.parties.map((party, i) => (
                                  <li key={i}><span className="text-foreground">{party.name}</span>{party.role && ` (${party.role})`}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {aiReview.values.length > 0 && (
                            <div>
                              <h5 className="text-sm font-medium mb-1">{t("wizard.aiReviewValues")}</h5>
                              <ul className="text-sm text-muted-foreground space-y-0.5">
                                {aiReview.values.map((item, i) => (
                                  <li key={i}>{item.label ? `${item.label}: ` : ""}<span className="text-foreground">{item.value}</span></li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {aiReview.dates.length > 0 && (
                            <div>
                              <h5 className="text-sm font-medium mb-1">{t("wizard.aiReviewDates")}</h5>
                              <ul className="text-sm text-muted-foreground space-y-0.5">
                                {aiReview.dates.map((item, i) => (
                                  <li key={i}>{item.label ? `${item.label}: ` : ""}<span className="text-foreground">{item.value}</span></li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {aiReview.penalties.length > 0 && (
                            <div>
                              <h5 className="text-sm font-medium mb-1">{t("wizard.aiReviewPenalties")}</h5>
                              <ul className="text-sm text-muted-foreground space-y-0.5 list-disc pl-4">
                                {aiReview.penalties.map((penalty, i) => <li key={i}>{penalty}</li>)}
                              </ul>
                            </div>
                          )}
                        </div>
                        {aiReview.missing.length > 0 && (
                          <div>
                            <h5 className="text-sm font-medium mb-1">{t("wizard.aiReviewMissing")}</h5>
                            <ul className="text-sm text-muted-foreground space-y-0.5 list-disc pl-4">
                              {aiReview.missing.map((item, i) => <li key={i}>{item}</li>)}
                            </ul>
                          </div>
                        )}
                        {aiReview.truncated && (
                          <p className="text-xs text-muted-foreground">{t("wizard.aiReviewTruncated")}</p>
                        )}
                        <div className="flex items-center justify-between gap-3 pt-1 border-t">
                          <p className="text-xs text-muted-foreground">{t("wizard.aiReviewDisclaimer")}</p>
                          {(aiReviewStatus === null || aiReviewStatus.unlimited || aiReviewStatus.remaining > 0) && (
                            <Button onClick={runAiReview} disabled={aiReviewLoading} variant="ghost" size="sm" className="gap-2 shrink-0">
                              {aiReviewLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Bot className="h-3 w-3" />}
                              {t("wizard.aiReviewAgain")}
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium mb-3">Signers ({signers.length})</h4>
                  <div className="space-y-2">
                    {signers.map((signer, index) => (
                      <div key={index} className="flex items-center gap-3 p-2 bg-muted rounded">
                        <Badge variant="secondary">{index + 1}</Badge>
                        <div className="flex-1">
                          <p className="font-medium text-sm">{signer.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {signer.email}
                            {signer.cpf && ` (CPF: ${signer.cpf})`}
                          </p>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {t("wizard.pageShort")} {(signer.signaturePageNumber || 0) + 1}
                        </Badge>
                        {signer.sendWhatsApp && signer.phone && (
                          <Badge variant="outline" className="text-green-600 border-green-300 bg-green-50 dark:bg-green-900/20">
                            <WhatsAppIcon />
                            <span className="ml-1">{signer.phone}</span>
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Document Preview with Signature Positions */}
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Eye className="h-4 w-4" />
                    {t("wizard.documentPreview") || "Document Preview with Signature Positions"}
                  </h4>
                  <div className="space-y-4">
                    {/* Page Navigation for Preview */}
                    <div className="flex items-center justify-center gap-4">
                      <Button
                        variant="outline"
                        size="default"
                        onClick={() => setPreviewPage(p => Math.max(0, p - 1))}
                        disabled={previewPage <= 0}
                        className="gap-2"
                      >
                        <ArrowLeft className="h-4 w-4" />
                        <span className="hidden sm:inline">{t("common.previous") || "Anterior"}</span>
                      </Button>
                      <div className="px-4 py-2 bg-primary/10 rounded-lg text-sm font-medium min-w-[120px] text-center">
                        <span className="text-primary">{t("wizard.page") || "Página"} {previewPage + 1}</span>
                        <span className="text-muted-foreground"> / {pdfPageCount}</span>
                      </div>
                      <Button
                        variant="outline"
                        size="default"
                        onClick={() => setPreviewPage(p => Math.min(pdfPageCount - 1, p + 1))}
                        disabled={previewPage >= pdfPageCount - 1}
                        className="gap-2"
                      >
                        <span className="hidden sm:inline">{t("common.next") || "Próxima"}</span>
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Keyboard navigation hint */}
                    <p className="text-xs text-center text-muted-foreground">
                      💡 {t("ui.use_the_arrow_keys_to_navigate_between_pages")}
                    </p>
                    
                    {/* PDF with Signature Markers - NO SCROLL */}
                    <div 
                      className="relative mx-auto bg-white border-2 border-muted rounded-xl shadow-lg"
                      style={{ 
                        width: '100%',
                        maxWidth: '500px',
                      }}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'ArrowLeft') {
                          e.preventDefault();
                          setPreviewPage(p => Math.max(0, p - 1));
                        } else if (e.key === 'ArrowRight') {
                          e.preventDefault();
                          setPreviewPage(p => Math.min(pdfPageCount - 1, p + 1));
                        }
                      }}
                    >
                      <PdfCanvas
                        src={getApiUrl(tempUpload.fileUrl)}
                        pageNumber={previewPage}
                        onPageCount={setPdfPageCount}
                        className="pointer-events-none"
                        style={{ width: '100%', minHeight: 0 }}
                      />
                      
                      {/* Signature Position Markers for current page */}
                      {signers.map((signer, index) => {
                        // Render only markers for the current page
                        if ((signer.signaturePageNumber || 0) !== previewPage) return null;
                        // X and Y are percentages (0-100)
                        const xPercent = signer.signaturePositionX ?? 50;
                        const yPercent = signer.signaturePositionY ?? 82;
                        return (
                          <div
                            key={index}
                            className="absolute z-10 pointer-events-none"
                            style={{
                              left: `${xPercent}%`,
                              top: `${yPercent}%`,
                              transform: 'translate(-50%, -100%)',
                            }}
                          >
                            <div className={cn(
                              "px-1.5 py-0.5 rounded border shadow-sm flex items-center gap-1 backdrop-blur-sm",
                              index === 0 && 'bg-blue-100/90 border-blue-500 text-blue-700',
                              index === 1 && 'bg-green-100/90 border-green-500 text-green-700',
                              index === 2 && 'bg-purple-100/90 border-purple-500 text-purple-700',
                              index >= 3 && 'bg-orange-100/90 border-orange-500 text-orange-700'
                            )}>
                              <Pen className="h-2 w-2" />
                              <span className="text-[9px] font-semibold whitespace-nowrap">
                                {signer.name.split(' ')[0]}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Legend */}
                    <div className="flex flex-wrap justify-center gap-3 text-xs">
                      {signers.map((signer, index) => (
                        <div key={index} className="flex items-center gap-1.5">
                          <div className={`w-3 h-3 rounded border-2 border-dashed
                            ${index === 0 ? 'bg-blue-100 border-blue-500' :
                              index === 1 ? 'bg-green-100 border-green-500' :
                              index === 2 ? 'bg-purple-100 border-purple-500' :
                              'bg-orange-100 border-orange-500'}
                          `} />
                          <span className="text-muted-foreground">
                            {signer.name} ({t("wizard.pageShort")} {(signer.signaturePageNumber || 0) + 1})
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                {message && (
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-medium mb-2">{t("wizard.message")}</h4>
                    <p className="text-sm text-muted-foreground">{message}</p>
                  </div>
                )}

                {isCompanyBrazilian && (
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-medium mb-2">{t("wizard.signatureMethod")}</h4>
                    <div className="flex items-center gap-3">
                      {selectedSignatureMethod === "govbr" ? (
                        <>
                          <div className="p-2 rounded-md bg-green-100 dark:bg-green-900/30">
                            <Shield className="h-5 w-5 text-green-600 dark:text-green-400" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{companyData?.enableGovBrManagement ? t("wizard.govbrPortalTitle") : t("wizard.govbrTitle")}</p>
                            <p className="text-xs text-muted-foreground">{companyData?.enableGovBrManagement ? t("wizard.govbrPortalSummary") : t("wizard.govbrSummary")}</p>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="p-2 rounded-md bg-blue-100 dark:bg-blue-900/30">
                            <Pen className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{t("wizard.standardTitle")}</p>
                            <p className="text-xs text-muted-foreground">{t("wizard.standardSummary")}</p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex justify-between gap-4">
                <Button variant="outline" onClick={() => goToStep(3)}>
                  <ArrowLeft className="mr-2 h-4 w-4" /> {t("common.back")}
                </Button>
                <Button onClick={handleSend} disabled={sending} data-testid="button-send">
                  {sending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="mr-2 h-4 w-4" />
                  )}
                  {t("wizard.send")}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        </div>
        </>
        )}
      </main>

      {/* GOV.BR Signature Method Dialog */}
      <Dialog open={showGovBrDialog} onOpenChange={(open) => {
        if (!open && tempUpload) {
          setStep(2);
        }
        setShowGovBrDialog(open);
      }}>
        <DialogContent className="sm:max-w-lg" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>{t("ui.choose_signature_method")}</DialogTitle>
            <DialogDescription>
              {t("ui.how_would_you_like_your_signers_to_sign_this_doc")}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <div 
              className={`p-4 border rounded-lg cursor-pointer hover-elevate ${selectedSignatureMethod === "govbr" ? "border-primary bg-primary/5" : ""}`}
              onClick={() => setSelectedSignatureMethod("govbr")}
              data-testid="option-govbr"
            >
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-md bg-green-100 dark:bg-green-900/30">
                  <Shield className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div className="flex-1">
                  {/* With GOV.BR management on, this option is the portal flow (assinador.iti.br); otherwise the name + CPF signature. */}
                  <p className="font-medium">{companyData?.enableGovBrManagement ? t("wizard.govbrPortalTitle") : t("wizard.govbrTitle")}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {companyData?.enableGovBrManagement ? t("wizard.govbrPortalDesc") : <>{t("ui.use_the_brazilian_government_s_digital_signature")}{" "}{t("ui.signers_will_authenticate_via_gov_br_account_sil")}</>}
                  </p>
                  <Badge variant="outline" className="mt-2 text-xs">
                    {companyData?.enableGovBrManagement ? t("wizard.govbrPortalBadge") : t("ui.legal_validity_in_brazil")}
                  </Badge>
                </div>
              </div>
            </div>
            <div 
              className={`p-4 border rounded-lg cursor-pointer hover-elevate ${selectedSignatureMethod === "draw" ? "border-primary bg-primary/5" : ""}`}
              onClick={() => setSelectedSignatureMethod("draw")}
              data-testid="option-regular"
            >
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-md bg-blue-100 dark:bg-blue-900/30">
                  <Pen className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1">
                  <p className="font-medium">{t("wizard.standardTitle")}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t("ui.signers_will_draw_or_type_their_signature_direct")}{" "}
                    {t("ui.quick_and_easy_for_any_recipient")}
                  </p>
                  <Badge variant="secondary" className="mt-2 text-xs">
                    {t("ui.recommended_for_most_use_cases")}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button 
              onClick={() => {
                setShowGovBrDialog(false);
                setStep(2);
              }}
              data-testid="button-continue-signature"
            >
              {t("wizard.continue")} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Leave Confirmation Dialog */}
      <AlertDialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("ui.leave_this_page")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("ui.are_you_sure_you_want_to_leave_your_progress_wil")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-stay">{t("wizard.stayOnPage")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmLeave} data-testid="button-leave">
              {t("ui.leave")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Signature Position Picker Modal */}
      <Dialog open={positionModalOpen} onOpenChange={setPositionModalOpen}>
        <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pen className="h-5 w-5 text-primary" />
              {t("wizard.setSignaturePosition") || "Definir Local da Assinatura"}
            </DialogTitle>
            <DialogDescription>
              {positioningSignerIndex !== null && signers[positioningSignerIndex] && (
                <span className="flex items-center gap-2">
                  {t("wizard.positioningFor") || "Posicionando assinatura para"}: 
                  <Badge variant="secondary">{signers[positioningSignerIndex].name || `Assinante ${positioningSignerIndex + 1}`}</Badge>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {/* Page Navigation */}
          <div className="flex items-center justify-center gap-4 py-2 border-b">
            <Button 
              variant="outline" 
              size="sm"
              disabled={tempPosition.page <= 0}
              onClick={() => setTempPosition(p => ({ ...p, page: p.page - 1 }))}
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              {t("common.previous") || "Anterior"}
            </Button>
            <span className="px-4 py-1 bg-muted rounded font-medium min-w-[100px] text-center">
              {t("wizard.page") || "Página"} {tempPosition.page + 1} / {pdfPageCount}
            </span>
            <Button 
              variant="outline" 
              size="sm"
              disabled={tempPosition.page >= pdfPageCount - 1}
              onClick={() => setTempPosition(p => ({ ...p, page: p.page + 1 }))}
            >
              {t("common.next") || "Próxima"}
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setTempPosition(p => ({ ...p, page: pdfPageCount - 1 }))}
              disabled={tempPosition.page === pdfPageCount - 1}
            >
              {t("ui.last_page")}
            </Button>
          </div>
          
          {/* PDF Preview with clickable/draggable positioning */}
          <div 
            ref={positionContainerRef}
            className="flex-1 overflow-auto bg-gray-100 dark:bg-gray-900 p-4 relative flex items-start justify-center" 
            style={{ maxHeight: '60vh' }}
          >
            {tempUpload && (
              <div 
                className={cn(
                  "relative bg-white shadow-lg transition-all duration-200",
                  !signaturePlaced && "cursor-crosshair",
                  signaturePlaced && !isDragging && "cursor-default"
                )}
                style={{ 
                  width: '100%',
                  maxWidth: '500px',
                  // No fixed aspect ratio: the container wraps the rendered canvas,
                  // so its bounding rect IS the page rect (Letter, A4, landscape...).
                  // Percentages measured here map 1:1 onto the real page in the backend.
                }}
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const relX = e.clientX - rect.left;
                  const relY = e.clientY - rect.top;
                  
                  // Store as PERCENTAGE (0-100) - independent of container size
                  // This way the position works regardless of responsive scaling
                  const xPercent = Math.max(12, Math.min(88, (relX / rect.width) * 100));
                  const yPercent = Math.max(3, Math.min(96, (relY / rect.height) * 100));
                  
                  // Round to 1 decimal place
                  setTempPosition(p => ({ 
                    ...p, 
                    x: Math.round(xPercent * 10) / 10, 
                    y: Math.round(yPercent * 10) / 10 
                  }));
                  
                  // Show placement animation
                  if (!signaturePlaced) {
                    setShowPlacementAnimation(true);
                    setTimeout(() => setShowPlacementAnimation(false), 500);
                  }
                  setSignaturePlaced(true);
                }}
              >
                {/* PDF Background */}
                <PdfCanvas
                        src={getApiUrl(tempUpload.fileUrl)}
                        pageNumber={tempPosition.page}
                        className="pointer-events-none"
                        style={{ width: '100%', minHeight: 0 }}
                      />
                
                {/* Instruction Overlay - shown before first click */}
                {!signaturePlaced && (
                  <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/5 pointer-events-none">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 mx-4 text-center animate-in fade-in zoom-in-95 duration-300 border-2 border-primary/20">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                        <Pen className="h-8 w-8 text-primary animate-bounce" />
                      </div>
                      <h3 className="text-lg font-semibold mb-2">
                        {t("wizard.clickToPlaceSignature") || "Clique para posicionar a assinatura"}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-3">
                        {t("wizard.clickInstructionDetail") || "Clique no local exato onde deseja que a assinatura apareça no documento."}
                      </p>
                      <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                        <span className="px-2 py-1 bg-muted rounded">
                          {t("wizard.tipDragAfter") || "💡 Após posicionar, você pode arrastar para ajustar"}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Draggable Signature Box */}
                {signaturePlaced && (
                  <div
                    className={cn(
                      "absolute z-10 cursor-grab active:cursor-grabbing select-none",
                      showPlacementAnimation && "animate-in zoom-in-50 duration-300",
                      isDragging && "scale-110 opacity-90"
                    )}
                    style={{
                      // X and Y are percentages (0-100)
                      left: `${tempPosition.x}%`,
                      top: `${tempPosition.y}%`,
                      transform: 'translate(-50%, -100%)',
                      transition: isDragging ? 'none' : 'all 0.15s ease-out',
                    }}
                    draggable={false}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsDragging(true);
                      
                      const container = e.currentTarget.parentElement;
                      if (!container) return;
                      
                      const handleMouseMove = (moveEvent: MouseEvent) => {
                        const rect = container.getBoundingClientRect();
                        const relX = moveEvent.clientX - rect.left;
                        const relY = moveEvent.clientY - rect.top;
                        const xPercent = Math.max(12, Math.min(88, (relX / rect.width) * 100));
                        const yPercent = Math.max(3, Math.min(96, (relY / rect.height) * 100));
                        setTempPosition(p => ({ 
                          ...p, 
                          x: Math.round(xPercent * 10) / 10, 
                          y: Math.round(yPercent * 10) / 10 
                        }));
                      };
                      
                      const handleMouseUp = () => {
                        setIsDragging(false);
                        document.removeEventListener('mousemove', handleMouseMove);
                        document.removeEventListener('mouseup', handleMouseUp);
                      };
                      
                      document.addEventListener('mousemove', handleMouseMove);
                      document.addEventListener('mouseup', handleMouseUp);
                    }}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      setIsDragging(true);
                      
                      const container = e.currentTarget.parentElement;
                      if (!container) return;
                      
                      const handleTouchMove = (moveEvent: TouchEvent) => {
                        const touch = moveEvent.touches[0];
                        const rect = container.getBoundingClientRect();
                        const relX = touch.clientX - rect.left;
                        const relY = touch.clientY - rect.top;
                        const xPercent = Math.max(12, Math.min(88, (relX / rect.width) * 100));
                        const yPercent = Math.max(3, Math.min(96, (relY / rect.height) * 100));
                        setTempPosition(p => ({ 
                          ...p, 
                          x: Math.round(xPercent * 10) / 10, 
                          y: Math.round(yPercent * 10) / 10 
                        }));
                      };
                      
                      const handleTouchEnd = () => {
                        setIsDragging(false);
                        document.removeEventListener('touchmove', handleTouchMove);
                        document.removeEventListener('touchend', handleTouchEnd);
                      };
                      
                      document.addEventListener('touchmove', handleTouchMove, { passive: false });
                      document.addEventListener('touchend', handleTouchEnd);
                    }}
                  >
                    <div className={cn(
                      "bg-primary/10 border border-dashed border-primary rounded shadow-sm flex items-center gap-1.5 transition-all whitespace-nowrap",
                      isDragging && "border-solid bg-primary/20 shadow-md border-2"
                    )}
                    style={{ width: "25%", minWidth: 90, maxWidth: 140, padding: "6px 8px", justifyContent: "center" }}
                    >
                      <Pen className="h-2.5 w-2.5 text-primary flex-shrink-0" />
                      <span className="text-[10px] font-medium text-primary">
                        {positioningSignerIndex !== null && signers[positioningSignerIndex]?.name 
                          ? signers[positioningSignerIndex].name.split(' ')[0]
                          : t("wizard.signature") || 'Assinatura'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="flex items-center justify-between p-4 border-t">
            <p className="text-xs text-muted-foreground">
              {signaturePlaced ? (
                <span className="flex items-center gap-2">
                  <Check className="h-3 w-3 text-green-500" />
                  Página {tempPosition.page + 1} • Posição definida
                </span>
              ) : (
                <span className="text-yellow-600">{t("ui.click_on_the_document_to_set_the_position")}</span>
              )}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setPositionModalOpen(false)}>
                {t("common.cancel") || "Cancelar"}
              </Button>
              <Button 
                onClick={savePosition} 
                disabled={!signaturePlaced}
                className="gap-2"
              >
                <Check className="h-4 w-4" />
                {t("wizard.confirmPosition") || "Confirmar"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Position Preview Dialog */}
      <Dialog open={aiPreviewOpen} onOpenChange={setAiPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5" /> {t("ui.ai_suggested_positions")}
            </DialogTitle>
            <DialogDescription>
              {tempUpload?.name} — {t("wizard.aiReviewDesc").replace("{pages}", String(aiPageCount))}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-1 gap-4 overflow-hidden min-h-0">
            {/* Signers sidebar */}
            <div className="w-52 flex-shrink-0 overflow-y-auto space-y-1 pr-2">
              {aiPositions.map((p, i) => {
                const COLORS = ["#90BE6D","#74C0FC","#F4A261","#E76F51","#6A4C93","#1982C4","#FF595E","#8AC926"];
                const color = COLORS[i % COLORS.length];
                const isOnCurrentPage = p.page === aiPreviewPage;
                return (
                  <div 
                    key={i} 
                    className={`flex items-center gap-2 p-2 rounded-md text-sm cursor-pointer transition-colors ${isOnCurrentPage ? 'bg-primary/10 ring-1 ring-primary/30' : 'bg-muted/50 opacity-60'}`}
                    onClick={() => setAiPreviewPage(p.page)}
                  >
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: color }} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{p.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {t("wizard.pageShort")} {p.page + 1} • {t(`wizard.confidence.${p.confidence === "high" || p.confidence === "medium" ? p.confidence : "low"}`)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* PDF preview with page navigation */}
            <div className="flex-1 flex flex-col gap-2 min-h-0">
              {/* Page nav */}
              {aiPageCount > 1 && (
                <div className="flex items-center justify-center gap-2 text-sm">
                  <Button variant="outline" size="sm" disabled={aiPreviewPage <= 0} onClick={() => setAiPreviewPage(p => p - 1)}>←</Button>
                  <span className="text-muted-foreground">{t("wizard.pageOf").replace("{page}", String(aiPreviewPage + 1)).replace("{total}", String(aiPageCount))}</span>
                  <Button variant="outline" size="sm" disabled={aiPreviewPage >= aiPageCount - 1} onClick={() => setAiPreviewPage(p => p + 1)}>→</Button>
                </div>
              )}
              {/* PDF + markers */}
              <div className="flex-1 bg-muted rounded-lg flex items-center justify-center overflow-auto p-4">
                {tempUpload && (tempUpload.type === "application/pdf" || tempUpload.type === "pdf" || tempUpload.name.endsWith(".pdf")) ? (
                  <div className="relative bg-white shadow-lg" style={{ width: "100%", maxWidth: 480 }}>
                    <PdfCanvas
                        src={getApiUrl(tempUpload.fileUrl)}
                        pageNumber={aiPreviewPage}
                        onPageCount={setAiPageCount}
                        className="pointer-events-none"
                        style={{ width: '100%', minHeight: 0 }}
                      />
                    {/* Position markers — only for current page */}
                    {aiPositions.filter(p => p.page === aiPreviewPage).map((p, i) => {
                      const globalIdx = aiPositions.indexOf(p);
                      const COLORS = ["#90BE6D","#74C0FC","#F4A261","#E76F51","#6A4C93","#1982C4","#FF595E","#8AC926"];
                      const color = COLORS[globalIdx % COLORS.length];
                      return (
                        <div
                          key={globalIdx}
                          className="absolute flex items-center gap-1 px-2 py-0.5 rounded whitespace-nowrap"
                          style={{
                            left: `${p.x}%`, top: `${p.y}%`,
                            transform: "translate(-50%,-100%)",
                            background: `${color}18`,
                            border: `1.5px dashed ${color}`,
                            zIndex: 10
                          }}
                        >
                          <div className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
                          <span className="text-[10px] font-semibold" style={{ color }}>{p.name.split(" ")[0]}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-2" />
                    <p className="text-sm">{t("wizard.wordNoPreview")}</p>
                    <p className="text-xs">{t("wizard.positionsOnSend")}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter className="flex justify-between sm:justify-between gap-2 pt-4">
            <Button variant="outline" onClick={() => setAiPreviewOpen(false)}>{t("ui.cancel")}</Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={adjustAiManually}>
                <Pen className="h-4 w-4 mr-2" /> {t("ui.adjust_manually")}
              </Button>
              <Button onClick={confirmAiPositions}>
                <Check className="h-4 w-4 mr-2" /> {t("wizard.confirmPositions")}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
